import React, {useEffect, useState} from 'react';
import {Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Typography} from '@mui/material';
import Grid from '@mui/material/Grid2';
import {useTranslation} from 'react-i18next';
import apiService from '@/service/api-service';
import {formatDate} from '@/utils/formatters';
import {ShiftData, ShiftsList} from '@/components/shifts';
import {useIsMobile} from "@/hooks/use-mobile.tsx";

interface AssignmentData {
  id: string;
  uuid: string;
  carVin: string;
  deviceManufacturerNumber: string;
  installedAt: any;
  removedAt: any;
  customerNumber?: string;
  [key: string]: any;
}

interface AssignmentDetailDialogProps {
  assignment: AssignmentData | null;
  open: boolean;
  onClose: () => void;
}

const AssignmentDetailDialog = ({ assignment, open, onClose }: AssignmentDetailDialogProps) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [shifts, setShifts] = useState<ShiftData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!assignment) return null;

  // Define fetchShifts outside of useEffect to avoid dependency issues
  const fetchShifts = async () => {
    if (!assignment.customerNumber || !assignment.uuid) return;

    setLoading(true);
    setError(null);

    try {
      const response = await apiService.get(
        `/customers/${assignment.customerNumber}/installations/${assignment.uuid}/shifts`
      );
      setShifts(response.data || []);
    } catch (err) {
      console.error('Failed to fetch shifts:', err);
      setError(t('errors.failedToLoadShifts'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && assignment && assignment.uuid) {
      fetchShifts();
    }
  }, [open, assignment?.uuid, assignment?.customerNumber]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth fullScreen={isMobile}>
      <DialogTitle sx={{ bgcolor: 'background.paper' }}>
        {t('assignments.details')}
      </DialogTitle>
      <DialogContent sx={{ bgcolor: 'background.paper', pt: 2 }}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="subtitle2" color="text.secondary">
              {t('assistant.vehicleIdentificationStep.vin')}
            </Typography>
            <Typography variant="body1" gutterBottom>
              {assignment.carVin}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="subtitle2" color="text.secondary">
              {t('devices.table.deviceSerial')}
            </Typography>
            <Typography variant="body1" gutterBottom>
              {assignment.deviceManufacturerNumber}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="subtitle2" color="text.secondary">
              {t('assignments.installationDate')}
            </Typography>
            <Typography variant="body1" gutterBottom>
              {formatDate(assignment.installedAt)}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="subtitle2" color="text.secondary">
              {t('assignments.removedAt')}
            </Typography>
            <Typography variant="body1" gutterBottom>
              {formatDate(assignment.removedAt)}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="subtitle2" color="text.secondary">
              {t('assignments.status')}
            </Typography>
            <Typography variant="body1" gutterBottom>
              {assignment.removedAt === null ? t('common.status.active') : t('common.status.inactive')}
            </Typography>
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        <Typography variant="h6" gutterBottom>
          {t('assignments.shifts')}
        </Typography>

        <ShiftsList
          shifts={shifts}
          loading={loading}
          error={error}
        />
      </DialogContent>
      <DialogActions sx={{ bgcolor: 'background.paper' }}>
        <Button onClick={onClose} color="primary">
          {t('common.close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssignmentDetailDialog;
